import { useState, useEffect } from 'react';

const ProductCard = ({ product }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const imageCount = product.images.length;

    // Auto-scroll logic: Changes image every 3 seconds
    useEffect(() => {
        if (imageCount <= 1) return; // Don't scroll if only 1 image

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === imageCount - 1 ? 0 : prevIndex + 1
            );
        }, 3000);

        return () => clearInterval(interval);
    }, [imageCount]);

    // Helper to handle dot logic (Max 5 dots shown)
    const getVisibleDots = () => {
        const maxDots = 5;
        if (imageCount <= maxDots) {
            return Array.from({ length: imageCount }, (_, i) => i);
        }

        // Sliding window logic for dots
        let start = Math.max(0, currentImageIndex - 2);
        let end = start + maxDots;

        if (end > imageCount) {
            end = imageCount;
            start = imageCount - maxDots;
        }

        return Array.from({ length: maxDots }, (_, i) => start + i);
    };

    return (
        <div className="bg-white rounded-2xl p-2 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col h-full z-10">

            {/* Image Carousel Container */}
            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 mb-4 group">
                {/* Images */}
                {product.images.map((src, index) => (
                    <img
                        key={index}
                        src={src}
                        alt={`${product.title} - view ${index + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                            }`}
                    />
                ))}

                {/* Floating Dots Indicator */}
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center space-x-1.5 bg-black/20 px-2 py-1 rounded-full backdrop-blur-[2px]">
                    {getVisibleDots().map((dotIndex) => (
                        <div
                            key={dotIndex}
                            className={`rounded-full transition-all duration-300 ${dotIndex === currentImageIndex
                                ? 'w-4 h-1.5 bg-white'     // Active Dash
                                : 'w-1.5 h-1.5 bg-white/60' // Inactive Dot
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Product Details */}
            <div className="flex flex-col flex-grow ">
                <h3 className="text-lg font-semibold text-black mb-4 line-clamp-1">{product.title}</h3>

                {/* Price & MOQ Row */}
                <div className="flex justify-between items-start mb-4 border-b border-gray-100 pb-3">
                    <div className="flex flex-col">
                        <span className="text-[14px] text-gray-400 tracking-wide">MOQ Units</span>
                        <span className="text-sm font-semibold text-gray-900">{product.moq || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="text-[14px] text-gray-400 tracking-wide">Est. Price</span>
                        <span className="text-sm font-bold text-gray-900 font-sans">{product.estPrice || 'N/A'}</span>
                    </div>
                </div>

                {/* Materials/Tags */}
                {(product.tags || product.materials) && (
                    <div className="mt-auto">
                        <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-2">
                            {product.tags ? 'Tags' : 'Available Materials'}
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {(product.tags || []).map((item, idx) => (
                                <span
                                    key={idx}
                                    className="inline-block px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-600 font-medium whitespace-nowrap"
                                >
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default ProductCard;